import { useRef, useState } from 'react';

/**
 * For use where you have a text <input> tag that will update on change but the
 * updates should be debounced.
 *
 * @usage
 * ```js
 * const Form = ({ textValue }) => {
 *   const [value, handleChange] = useInputDebounceCallback(
 *     textValue, // Initial value
 *     newValue => runLongAction(newValue) // Debounced callback
 *   )
 *
 *   return (
 *     <div>
 *       <input type="text" value={value} onChange={handleChange} />
 *     </div>
 *   );
 * }
 * ```
 *
 * @template {string|number} T
 * @param {T} initialValue
 * @param {((value: T) => void)} callback
 * @param {number=} timeout Time timeout in ms, defaults to 250
 * @returns {[T, (event: T|React.ChangeEvent<HTMLInputElement>) => void]}
 */
export function useInputDebounceCallback(
  initialValue,
  callback,
  timeout = 250
) {
  const updateTimer = useRef(null);
  const [value, setValue] = useState(initialValue);

  /**
   * @param {T | React.ChangeEvent<HTMLInputElement>} event
   */
  const handler = event => {
    let newValue;
    if (typeof event === 'string' || typeof event === 'number') {
      newValue = event;
    } else {
      newValue = event.target.value;
    }

    if (newValue === value) return;

    setValue(newValue);

    if (updateTimer.current) clearTimeout(updateTimer.current);
    updateTimer.current = setTimeout(() => {
      updateTimer.current = null;
      callback(newValue);
    }, timeout);
  };

  return [value, handler];
}
